// src/hooks/useFileActionHandler.ts
import { useCallback } from 'react';
import { ChonkyActions, ChonkyFileActionData, FileHelper } from 'chonky';
import { FileNode, CustomFileMap } from '../types/FileManagerTypes';
import { FileData } from 'chonky';
import { FileAction } from '../enums/fileActions';

type FilesArg = FileNode[] | undefined;

export const useFileActionHandler = (
  setCurrentFolderId: (folderId: string) => void,
  handleBulkDownload: (files: FileNode[]) => void,
  showReupload: (files?: FilesArg) => void,
  showPreview: (files?: FilesArg) => void,
  showMove: (files?: FilesArg) => void,
  showTrashConfirmation: (files?: FilesArg) => void,
  showRestoreTrash: (files?: FilesArg) => void,
  showDeleteConfirmation: (files?: FilesArg) => void,
  showPermissions: (files?: FilesArg) => void,
  handleFileOpen: (file: FileData) => void,
  createImageLayer: (imageId: FileNode[]) => void,
  removeImageLayer: (imageId: FileNode[]) => void,
  handleFileSelection: (files: FileNode[]) => void,
) => {
  return useCallback(
    (data: ChonkyFileActionData, _: CustomFileMap) => {
      const selected = (data.state?.selectedFilesForAction as FileNode[] | undefined) ?? [];

      // OpenFiles is the one built-in Chonky action still in use -- fired by
      // <FileNavbar> breadcrumb clicks to navigate into a folder.
      if (data.id === ChonkyActions.OpenFiles.id) {
        const { targetFile, files } = data.payload;
        const fileToOpen = targetFile ?? files[0];
        handleFileOpen(fileToOpen);
        if (fileToOpen && FileHelper.isDirectory(fileToOpen)) {
          setCurrentFolderId(fileToOpen.id);
        }
        return;
      }

      const actionMap: Partial<Record<FileAction, (files: FileNode[]) => void>> = {
        [FileAction.CREATE_IMAGE_LAYER]: createImageLayer,
        [FileAction.REMOVE_IMAGE_LAYER]: removeImageLayer,
        [FileAction.DELETE_FILES]: showTrashConfirmation,
        [FileAction.DELETE_ALL]: showTrashConfirmation,
        [FileAction.REUPLOAD_FILE]: showReupload,
        [FileAction.PREVIEW_FILE]: showPreview,
        [FileAction.MOVE_FILE]: showMove,
        [FileAction.MOVE_ALL]: showMove,
        [FileAction.RESTORE_FILE]: showRestoreTrash,
        [FileAction.RESTORE_ALL]: showRestoreTrash,
        [FileAction.PERMANENTLY_DELETE_FILE]: showDeleteConfirmation,
        [FileAction.PERMANENTLY_DELETE_ALL]: showDeleteConfirmation,
        [FileAction.DOWNLOAD_FILES]: handleBulkDownload,
        [FileAction.DOWNLOAD_ALL]: handleBulkDownload,
        [FileAction.CHANGE_SELECTION]: handleFileSelection,
        [FileAction.FILE_PERMISSIONS]: showPermissions,
      };

      actionMap[data.id as FileAction]?.(selected);
    },
    [
      setCurrentFolderId,
      createImageLayer,
      removeImageLayer,
      handleFileSelection,
      showReupload,
      showPreview,
      showMove,
      showTrashConfirmation,
      showRestoreTrash,
      showDeleteConfirmation,
      showPermissions,
      handleFileOpen,
      handleBulkDownload,
    ],
  );
};
